import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnInit, Renderer2, ViewChild, afterNextRender, computed, effect, inject, input, output, signal, DOCUMENT } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { DynamicHeaderCommon } from 'src/app/components/dynamic-header/category/dynamic-header-common.class';
import { DynamicMenuCollapseComponent } from 'src/app/components/dynamic-menu-collapse/dynamic-menu-collapse.component';
import { SearchBarComponent } from 'src/app/components/search-suggestion/components/search-bar/search-bar.component';
import { StaticMenuCollapseComponent } from 'src/app/components/static-menu-collapse/static-menu-collapse.component';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { ListParams } from 'src/app/core/models/list-params.model';
import { AdsManagementService } from 'src/app/core/services/adsManagement/ads-management.service';
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { Cart } from 'src/app/core/services/cart/cart-api.service';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { createDefaultHeaderData } from 'src/app/core/utilities/type-defaults.util';
import { ProductListing, ProductListingHit } from 'src/app/products/models/product-listing.model';
import { PriceService } from 'src/app/products/services/price.service';
import { ProductsRepositoryService } from 'src/app/products/services/products-repository.service';
import { IndianCurrencyPipe } from 'src/app/shared/pipes/indian-currency.pipe';
import { Category } from 'src/app/shared/services/common-api.service';
import { MenuCollapseService } from '../../services/menu-collapse.service';
import { HeaderData } from '../header-data';

declare var bootstrap: any; // Bootstrap JS available globally

@Component({
  selector: 'category-layout-11',
  standalone: true,
  imports: [CommonModule, RouterModule, SearchBarComponent, StaticMenuCollapseComponent, DynamicMenuCollapseComponent, IndianCurrencyPipe],
  templateUrl: './layout-11.component.html',
  styleUrls: ['./layout-11.component.scss']
})
export class Layout11Component extends DynamicHeaderCommon implements OnInit, AfterViewInit {

  @ViewChild('changeHeaderElement') declare changeHeaderElement: ElementRef;
  @ViewChild('changeHeaderMobileElement') declare changeHeaderMobileElement: ElementRef;
  @ViewChild('dynamicCategoryHeader') declare dynamicCategoryHeader: ElementRef;
  @ViewChild('dynamicCategoryHeaderMobile') declare dynamicCategoryHeaderMobile: ElementRef;

  readonly dynamicHeaderInline = input<boolean>(false);
  readonly dynamicHeaderHover = input<boolean>(false);
  readonly layoutType = input<string>('');
  readonly domain = input<string>('');
  readonly category = input<Category[] | null>(null);
  readonly clickedElementAvailable = input<any>(null);
  readonly cart = input<Cart | null>(null);
  readonly selectedDeviceTypeInput = input<string>('');
  readonly headerDataInput = input<HeaderData | null>(null);

  readonly logout$ = output<boolean>();

  // Internal signals for state management
  readonly isSearchOverlaySignal = signal<boolean>(false);
  readonly showActiveColorSignal = signal<boolean>(false);
  readonly currentMenuIdSignal = signal<string>('');
  readonly showSubcategoryPanelSignal = signal<boolean>(false);
  readonly mobileSubCategorySignal = signal<Category[]>([]);
  readonly showMobileSearchPanelSignal = signal<boolean>(false);

  // Legacy properties for compatibility
  isSubmenuAvailable = true;
  override isToggled = false;
  imageUrls = IMAGE_URLS;
  UTM_MEDIUM = 'menu';
  private collapseInstances: Map<string, any> = new Map();
  category_url_paths: string = '';
  category_name: string = '';

  // âœ… Angular 20: Signal-based state for category products
  private headerCategoryProductsSignal = signal<{ [key: string]: any[] }>({});
  readonly headerCategoryProducts = computed(() => this.headerCategoryProductsSignal());

  loading = new BehaviorSubject<boolean>(false);
  loading$ = this.loading.asObservable();
  categoryMenu = new Subject<{ utm_medium: string, utm_source: string, category_name: string }>();
  categoryMenu$ = this.categoryMenu.asObservable();
  hoveredCatId: string = '';

  // âœ… Angular 20: Computed signal for top products
  readonly topProducts = computed(() => {
    return this.headerCategoryProducts()[this.category_url_paths]?.slice(0, 4);
  });

  // Fallback for HeaderData
  get headerDataFallback(): HeaderData {
    return this.headerDataInput() || createDefaultHeaderData();
  }

  constructor(
    protected override menuCollapseService: MenuCollapseService,
    protected override userService: UserService,
    protected override renderer: Renderer2,
    protected override elementRef: ElementRef,
    @Inject(DOCUMENT) protected override document: Document,
    protected override platformService: PlatformService,
    private productsRepositoryService: ProductsRepositoryService,
    private adsManagementService: AdsManagementService,
    private storeConfigurationRepositoryService: StoreConfigurationRepositoryService,
    private router: Router,
    private priceService: PriceService,
    private notificationService: NotificationService,
  ) {
    super(
      renderer,
      menuCollapseService,
      elementRef,
      platformService,
      document,
      inject(BrowserApiService)
    );

    // Effects for input synchronization
    effect(() => {
      const headerDataValue = this.headerDataInput();
      if (headerDataValue) {
        this.addHoverForMenu();
      }
    });

    effect(() => {
      const deviceType = this.selectedDeviceTypeInput();
      if (deviceType === this.device.desktop) {
        this.menuCollapseService.setHamburgerMenu(false);
        this.showMobileSearchPanelSignal.set(false);
      }
      this.repositionHeaderMenu();
    });

    effect(() => {
      const cartValue = this.cart();
      // Handle cart updates if needed
    });

    effect(() => {
      const categoryValue = this.category();
      // âœ… Angular 20: When category data is available, attach hover listeners
      if (categoryValue && categoryValue.length > 0) {
        afterNextRender(() => {
          this.addHoverForMenu();
        });
      }
    });

    effect(() => {
      const clickedElementValue = this.clickedElementAvailable();
      // Handle clicked element updates if needed
    });
  }

  ngOnInit(): void {
    if (this.platformService.isBrowser()) {
      // âœ… REMOVED: setTimeout - now handled by category() effect in constructor
    }

    this.categoryMenu$.pipe(
      tap((val: any) => {
        this.category_name = val?.category_name;
        this.category_url_paths = val?.utm_medium;
        this.loading.next(false);
      }),
      distinctUntilChanged((prev, curr) =>
        prev.utm_medium === curr.utm_medium &&
        prev.category_name === curr.category_name
      ),
      filter((val: any) => !this.headerCategoryProducts.hasOwnProperty(val.utm_medium)),
    ).subscribe((val: any) => {
      this.getProductList({
        utm_medium: this.category_url_paths,
        utm_source: 'menu',
        category_name: this.category_name
      })
    })
  }

  async getProductList(event: { utm_medium: string, utm_source: string, category_name: string }) {
    this.category_url_paths = event.utm_medium;
    this.category_name = event.category_name;

    // âœ… Angular 20: Check signal value
    if (this.headerCategoryProducts().hasOwnProperty(this.category_url_paths)) {
      return;
    }

    this.loading.next(true);

    let showOutOfStockProducts = this.storeConfigurationRepositoryService.showOutOfStockProducts;

    const collectedUtmAndCodeDetails = this.adsManagementService.getCollectedUtmAndCodeDetails();

    let filters: any = {};

    // If showOutOfStockProducts value is false then in_stock set to true
    if (!showOutOfStockProducts) {
      filters.in_stock = true;
    }

    if (this.category_url_paths && this.category_url_paths !== '') {
      filters.category_url_paths = this.category_url_paths;
    }

    const updatedFilters = {
      ...filters,
      ...(collectedUtmAndCodeDetails && Object.keys(collectedUtmAndCodeDetails)?.length && {
        attribution_data: JSON.stringify(collectedUtmAndCodeDetails || {})
      })
    };

    const params = new ListParams('', '', '', '', 0, 5, '', updatedFilters);

    // âœ… Angular 20: Use async/await with signal-based service
    await this.productsRepositoryService.getProductsV2(params);
    const response = this.productsRepositoryService.productsV2();

    // Check for service errors after operation
    const serviceError = this.productsRepositoryService.serviceError();
    if (serviceError) {
      this.loading.next(false);
      return;
    }

    // âœ… Angular 20: Check signal value before updating
    if (!this.headerCategoryProducts().hasOwnProperty(this.category_url_paths)) {
      if (response && response.hits && response.hits.hits) {
        const productListingHit = response.hits.hits as ProductListingHit[];

        const processedProducts = productListingHit.map(hit => {
          let productListing = hit._source as ProductListing;

          if (productListing?.variants && productListing.variants.length > 0) {
            const productVariant = productListing.variants?.filter(product => product.is_active && product.is_visible)[0];

            if (productVariant) {
              const { actualPrice, sellingPrice } = this.priceService?.getProductListingPrice(productVariant);
              return { ...productVariant, actualPrice, sellingPrice };
            }
          }
          return null;

        }).filter(product => product !== null);

        // âœ… Angular 20: Update signal immutably
        this.headerCategoryProductsSignal.update(products => ({
          ...products,
          [this.category_url_paths]: processedProducts
        }));
      } else {
        // âœ… Angular 20: Update signal immutably
        this.headerCategoryProductsSignal.update(products => ({
          ...products,
          [this.category_url_paths]: []
        }));
      }
    }
    this.loading.next(false);
  }

  getCategoryMenu(event: { utm_medium: string, utm_source: string, category_name: string }) {
    if (event.utm_medium) {
      this.categoryMenu.next(event);
    }
  }

  ngAfterViewInit(): void {
    if (this.platformService.isBrowser()) {
      this.repositionHeaderMenu();
      this.applyFocusEvent();
    }
  }

  onLinkClick(): void {
    const subMenus = document.getElementsByClassName("hide-sub-menu-header");
    // Iterate over the elements and set display to none
    Array.from(subMenus).forEach(subMenu => {
      this.renderer.setStyle(subMenu, 'display', 'none');
    });
  }

  override toggleSearchDropDown() {
    this.isSearchOverlaySignal.set(!this.isSearchOverlaySignal());
    this.menuCollapseService.setHamburgerMenu(false);
    this.searchInputControl.reset();
    if (this.isSearchOverlaySignal()) {
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    } else {
      this.renderer.removeStyle(this.document.body, 'overflow');
    }
  }

  override toggleHamburgerMenu() {
    this.isToggled = true;
    this.menuCollapseService.toggleHamburgerMenu();
    this.isSearchOverlaySignal.set(false);
    if (this.menuCollapseService.isHamburgerMenu()) {
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    } else {
      this.renderer.removeStyle(this.document.body, 'overflow');
    }
  }

  searchItems() {
    this.toggleSearchDropDown();
  }

  subMenu() {
    this.isSubmenuAvailable = true;
  }

  openSubcategoryPanel(sub_categories: Category[] = []) {
    if (sub_categories?.length) {
      this.showSubcategoryPanelSignal.set(!this.showSubcategoryPanelSignal());
      this.mobileSubCategorySignal.set(sub_categories);
    } return
  }

  toggleAccordion(index: string | number, isOpen: boolean) {
    const collapseEl = document.getElementById('collapse' + index);
    if (collapseEl) {
      const key = 'collapse' + index;

      // Create instance only once per accordion
      if (!this.collapseInstances.has(key)) {
        this.collapseInstances.set(key, new bootstrap.Collapse(collapseEl, { toggle: false }));
      }

      const collapseInstance = this.collapseInstances.get(key);

      if (isOpen) {
        collapseInstance.show();
        this.hoveredCatId = index as string;

      } else {
        collapseInstance.hide();
        this.hoveredCatId = '';
      }
    }
  }

  openProductPage(urlPath: string) {
    if (urlPath) {
      this.router.navigate(['/products/', urlPath]);
      this.onLinkClick();
    } else {
      this.notificationService.showError('Product URL is required');
    }
  }

  viewAllProducts() {
    this.router.navigate(['/category/', this.category_url_paths], { queryParams: { utm_source: 'menu', utm_medium: this.category_url_paths } });
    this.onLinkClick();
  }

  // Implement searchProduct method to handle search input events
  override searchProduct(searchQuery: { keyboardEvent: KeyboardEvent, suggestionItem: any, searchInputValue?: string }): void {
    const { keyboardEvent, suggestionItem } = searchQuery;

    if (suggestionItem) {
      this.searchQuery$.emit(searchQuery);
      this.isSearchOverlaySignal.set(false); // Close panel after suggestion selection
      this.renderer.removeStyle(this.document.body, 'overflow');
    } else if (keyboardEvent) {
      if (keyboardEvent.key === 'Enter') {
        this.searchQuery$.emit({ ...searchQuery, searchInputValue: this.searchInputControl?.value || '' });
        this.isSearchOverlaySignal.set(false); // Close panel after Enter key press
        this.renderer.removeStyle(this.document.body, 'overflow');
      }
    }
  }
}
